import React, { useCallback, useEffect, useMemo, useState } from "react";
import Countdown, { zeroPad } from "react-countdown";
import Loader from "@druva-apps/orion/lib/loader";
import Row from "@druva-apps/orion/lib/row";
import Col from "@druva-apps/orion/lib/col";
import Button from "@druva-apps/orion/lib/button";
import Modal from "@druva-apps/orion/lib/modal";
import styles from "./loginContainer.module.scss";
import VerificationForm from "./VerificationForm";
import RegisterMobileForm from "./RegisterMobileForm";
import BackButton from "../BackButton";
import FormCardBody from "../FormCardBody";
import { formatTotpSecretForDisplay, totpSecretToDataUrl } from "utils/totpQr";
import { downloadMfaTotpConfigurationPdf } from "utils/totpPdf";
import useMfaEnroll from "queryHooks/auth/useMfaEnroll";
import useMfaSetupSms from "queryHooks/auth/useMfaSetupSms";
import useMfaSetupSmsResend from "queryHooks/auth/useMfaSetupSmsResend";
import useMfaSoftwareTokenQuery from "queryHooks/auth/useMfaSoftwareTokenQuery";
import {
  MFA_SESSION_EXPIRED_MESSAGE,
  MFA_SETUP_AND_AUTHENTICATOR_OTP_TTL_MS,
} from "authSessionConfig";

const SETUP_SESSION_EXPIRE_LABEL = "The session will expire in";

const TotpSetupModal = ({
  isOpen,
  onCancel,
  onContinue,
  qrDataUrl,
  secretFormatted,
  onDownloadPdf,
}) => (
  <Modal
    isOpen={isOpen}
    toggle={onCancel}
    header="Set up Authenticator App"
    size="lg"
    dataTestId="mfa-totp-setup-modal"
    body={() => (
      <div>
        <p className="oui-text-body oui-mb-3">
          Authenticator app based multifactor authentication is enabled for your
          organization.
        </p>
        <Row>
          <Col xs="auto">
            <div className="oui-border oui-rounded oui-shadow-sm oui-p-2 oui-mr-3 oui-mb-2">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="TOTP QR Code"
                  data-testid="mfa-setup-qr"
                />
              ) : (
                <Loader isLoading />
              )}
            </div>
          </Col>
          <Col>
            <div className="oui-flex-grow-1">
              <p className="oui-font-weight-bold oui-mb-2 oui-text-body">
                Follow below steps to configure authenticator app
              </p>
              <ol className="oui-pl-3 oui-text-body">
                <li>Download the &apos;Authenticator app&apos;.</li>
                <li>In app, select &apos;set up an account&apos;.</li>
                <li>
                  Scan the given QR code or manually enter below code in the
                  app, to obtain 6 digit OTP.
                </li>
              </ol>
              <p
                className="oui-font-weight-bold oui-mt-2 oui-text-body oui-text-monospace"
                data-testid="mfa-setup-secret"
              >
                {secretFormatted}
              </p>
              <p className="oui-text-muted oui-mt-2">
                Note : After successful configuration, make a note of above code
                or{" "}
                <a
                  href="/"
                  data-testid="download-pdf-btn"
                  className="oui-btn-link"
                  onClick={(e) => {
                    e.preventDefault();
                    onDownloadPdf();
                  }}
                >
                  download
                </a>{" "}
                the QR code pdf file for future reference.
              </p>
            </div>
          </Col>
        </Row>
      </div>
    )}
    footer={() => (
      <div className="oui-d-flex oui-justify-content-end oui-w-100">
        <Button label="Cancel" onClick={onCancel} />
        <Button
          label="Continue"
          type="primary"
          className="oui-ml-2"
          onClick={onContinue}
        />
      </div>
    )}
  />
);

const MfaSetupPanel = ({
  accessToken,
  email,
  mfaSetupMode,
  sessionExpiresAt,
  onComplete,
  onBack,
}) => {
  const isSms = mfaSetupMode === "SMS";
  const [smsPhase, setSmsPhase] = useState("phone");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [totpSetupModalDismissed, setTotpSetupModalDismissed] = useState(false);

  const enrollExpiredDate = useMemo(() => {
    if (sessionExpiresAt != null) {
      return sessionExpiresAt;
    }
    // Fallback if parent did not set anchor (should not happen on normal login flow).
    return Date.now() + MFA_SETUP_AND_AUTHENTICATOR_OTP_TTL_MS;
  }, [sessionExpiresAt]);

  const {
    data: totpData,
    isLoading: totpLoading,
    isError: totpError,
  } = useMfaSoftwareTokenQuery(accessToken, !isSms);

  const softwareToken = totpData?.softwareToken || "";
  const secretFormatted = formatTotpSecretForDisplay(softwareToken);

  const { mutateAsync: enroll, isLoading: enrollLoading } = useMfaEnroll();
  const { mutateAsync: setupSms, isLoading: smsSendLoading } = useMfaSetupSms();
  const { mutateAsync: resendSms, isLoading: smsResendLoading } =
    useMfaSetupSmsResend();

  useEffect(() => {
    setTotpSetupModalDismissed(false);
  }, [softwareToken]);

  useEffect(() => {
    if (!softwareToken || isSms) {
      setQrDataUrl("");
      return;
    }
    let cancelled = false;
    (async () => {
      const dataUrl = await totpSecretToDataUrl(email, softwareToken, {
        width: 220,
      });
      if (!cancelled) setQrDataUrl(dataUrl);
    })();
    return () => {
      cancelled = true;
    };
  }, [email, isSms, softwareToken]);

  const showTotpSetupModal =
    !isSms && !!qrDataUrl && !!softwareToken && !totpSetupModalDismissed;

  const dismissTotpModal = useCallback(() => {
    setTotpSetupModalDismissed(true);
  }, []);

  const handleDownloadPdf = useCallback(() => {
    if (!qrDataUrl || !softwareToken) return;
    void downloadMfaTotpConfigurationPdf({
      qrCodeDataUrl: qrDataUrl,
      softwareToken,
    });
  }, [qrDataUrl, softwareToken]);

  const submitEnroll = useCallback(
    async ({ mfaCode }) => {
      const result = await enroll({
        accessToken,
        confirmationCode: mfaCode,
        // 0 - EMAIL, 1 - TOTP, 2 - SMS, 3 - NONE
        mfaMode: isSms ? 2 : 1,
      });
      onComplete(result);
    },
    [accessToken, enroll, isSms, onComplete]
  );

  const onRegisterMobile = useCallback(
    async ({ phoneNumber }) => {
      await setupSms({ accessToken, phoneNumber });
      setSmsPhase("otp");
    },
    [accessToken, setupSms]
  );

  const onResendSms = useCallback(async () => {
    await resendSms({ accessToken });
  }, [accessToken, resendSms]);

  if (!isSms && (totpLoading || (!totpError && !softwareToken))) {
    return (
      <div className="oui-card-body oui-d-flex oui-justify-content-center oui-p-4">
        <Loader isLoading />
      </div>
    );
  }

  if (!isSms && totpError) {
    return (
      <FormCardBody
        footer={<BackButton onClick={onBack} label="Go to Login" />}
      >
        <p className="oui-text-danger oui-mb-3">
          Unable to start authenticator setup. Please try again.
        </p>
      </FormCardBody>
    );
  }

  return (
    <>
      <FormCardBody style={enrollLoading ? { display: "none" } : {}}>
        {!isSms ? (
          <TotpSetupModal
            isOpen={showTotpSetupModal}
            onCancel={onBack}
            onContinue={dismissTotpModal}
            qrDataUrl={qrDataUrl}
            secretFormatted={secretFormatted}
            onDownloadPdf={handleDownloadPdf}
          />
        ) : smsPhase === "phone" ? (
          <>
            {sessionExpiresAt != null ? (
              <div
                className="oui-mb-2 oui-small"
                data-testid="mfa-setup-sms-session-countdown"
              >
                <Countdown
                  date={sessionExpiresAt}
                  renderer={({ minutes, seconds, completed }) =>
                    completed ? (
                      <span className="oui-text-danger">
                        Setup session expired. Return to login and try again.
                      </span>
                    ) : (
                      <>
                        <span className="oui-text-muted">
                          {SETUP_SESSION_EXPIRE_LABEL}{" "}
                        </span>
                        <span>
                          {minutes}:{zeroPad(seconds)}
                        </span>
                      </>
                    )
                  }
                />
              </div>
            ) : null}
            <RegisterMobileForm
              compact
              onSubmit={onRegisterMobile}
              onBack={onBack}
              isSubmitting={smsSendLoading}
            />
          </>
        ) : null}

        {(!isSms && softwareToken && totpSetupModalDismissed) ||
        (isSms && smsPhase === "otp") ? (
          <VerificationForm
            setStep={() => {}}
            onBack={onBack}
            onSubmit={submitEnroll}
            expiredDate={enrollExpiredDate}
            instructionText={
              isSms
                ? "Enter OTP sent on your registered mobile number"
                : "Enter OTP generated on the authenticator app"
            }
            sessionExpireLabel={
              isSms
                ? "The activation OTP will expire in"
                : SETUP_SESSION_EXPIRE_LABEL
            }
            resendLabel={isSms ? "Resend OTP" : null}
            onResend={isSms ? onResendSms : undefined}
            showDidNotReceiveLink={false}
            backLabel="Go to Login"
            verifyDisabled={enrollLoading || smsResendLoading}
            codeExpiredMessage={MFA_SESSION_EXPIRED_MESSAGE}
            compact
          />
        ) : null}
      </FormCardBody>

      {enrollLoading ? (
        <div
          className="oui-card-body oui-d-flex oui-align-items-center oui-justify-content-center"
          data-testid="loader-wrapper"
        >
          <Loader isLoading>
            <div className={styles.login_loader} />
          </Loader>
        </div>
      ) : null}
    </>
  );
};

MfaSetupPanel.defaultProps = {
  mfaSetupMode: "TOTP",
};

export default MfaSetupPanel;
